import { type CartCouponEffectCode, CartNotificationAction } from '@libs/core';

interface BaseCartNotification {
    sku: string;
    lineItemId: string;
}

export type { CartCouponEffectCode } from '@libs/core';

export interface CartNotificationQtyUpdate extends BaseCartNotification {
    action: CartNotificationAction.LINE_ITEM_QTY_UPDATE;
    maxOrderQty: number;
}

export interface CartNotificationAutoshipRemoved extends BaseCartNotification {
    action: CartNotificationAction.LINE_ITEM_AUTOSHIP_REMOVED;
    productName: string;
}

export interface CartNotificationScheduledDeliveryDateUpdated extends BaseCartNotification {
    action: CartNotificationAction.SCHEDULED_DELIVERY_DATE_UPDATED;
    scheduledDeliveryDate: string | null;
}

/** Cart-level promo + Autoship messaging (CPC-6938); no line-item fields. */
export interface CartNotificationCouponEffect {
    action: CartNotificationAction.COUPON_EFFECT;
    couponCode: string;
    code: CartCouponEffectCode;
    type: 'error' | 'info';
}

export interface CartNotificationSddInvalidDeliveryWindow {
    notificationType: 'SddInvalidDeliveryWindow';
    action: CartNotificationAction.DELIVERY_WINDOW_INVALID;
    errorCode?: string;
    message?: string;
}

export const isCartNotificationSddInvalidDeliveryWindow = (
    n: CartNotification
): n is CartNotificationSddInvalidDeliveryWindow => n.action === CartNotificationAction.DELIVERY_WINDOW_INVALID;

export const isCartNotificationCouponEffect = (
    notification: CartNotification
): notification is CartNotificationCouponEffect => notification.action === CartNotificationAction.COUPON_EFFECT;

export interface CartNotificationDiscontinuedItemsRemoved {
    action: CartNotificationAction.DISCONTINUED_ITEMS_REMOVED;
    productNames: string[];
    wasShown?: boolean;
}

export const isCartNotificationDiscontinuedItemsRemoved = (
    n: CartNotification
): n is CartNotificationDiscontinuedItemsRemoved => n.action === CartNotificationAction.DISCONTINUED_ITEMS_REMOVED;

export interface CartNotificationTreatPointsRemoved {
    notificationType: 'TreatPointsRemoved';
    action: CartNotificationAction.TREAT_POINTS_REMOVED;
    errorCode?: string;
    errorMessage?: string;
}

export const isCartNotificationTreatPointsRemoved = (n: CartNotification): n is CartNotificationTreatPointsRemoved =>
    n.action === CartNotificationAction.TREAT_POINTS_REMOVED;

export type CartNotification =
    | CartNotificationQtyUpdate
    | CartNotificationAutoshipRemoved
    | CartNotificationScheduledDeliveryDateUpdated
    | CartNotificationCouponEffect
    | CartNotificationSddInvalidDeliveryWindow
    | CartNotificationDiscontinuedItemsRemoved
    | CartNotificationTreatPointsRemoved;
